import Backbone from 'backbone'
import _ from 'lodash'
import template from './file-library.hbs'
import guard from 'scoreboard/components/unsaved/guard'
import conductor from 'scoreboard/components/conductor'
import globals from 'scoreboard/components/globals'
import i18n from 'scoreboard/components/i18n'
import icon from 'scoreboard/components/icon'
import FormView from 'scoreboard/components/form-view'
import UploadForm from './upload-form'
import File from './file'
import appDisplay from 'scoreboard/components/app-display'
import Tooltip from 'scoreboard/components/tooltip'
import permissions from 'scoreboard/components/permissions'
import projects from 'scoreboard/projects/projects'

var FileLibrary = Backbone.View.extend({

	className: 'file-library',

	tagName: 'form',

	/**
	 * File library for choosing images or spreadsheets (originally added for
	 * #20152).
	 *
	 * https://user-images.githubusercontent.com/1259395/46669669-15741300-cb9e-11e8-8799-9beeea0cebe3.png
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Function} options.ObjectGardener the Gardener constructor to use
	 * for the object selector, which is the selector on the right side of the
	 * dialog
	 *
	 * @param {Function} options.ObjectSelector the ObjectSelector constructor
	 * to use
	 */
	initialize: function(options = {}) {
		this.options = options

		let MyFormView = FormView.extend({
			test: this.test.bind(this)
		})

		this.formView = new MyFormView({
			el: this.el,
			submit: this.submit.bind(this)
		})
	},

	events: {
		'click .go-to-documents': 'goToDocuments',
		'click .all-images': 'allImages',
		'click .upload-image': 'uploadImage',
		'click .button-primary': 'done'
	},

	render: function() {
		this.$el.empty().append(template())
		this.renderObjectSelector()
		this.formView.start()
		return this
	},

	renderObjectSelector: function() {
		this.objectSelector = new this.options.ObjectSelector({
			orgId: globals.orgId,
			projectId: projects.id,
			showProjectSelector: true
		})
		this.$('.object-selector-container').append(this.objectSelector.el)
		this.objectSelector.render()
		this.objectSelector.on('select selectOrg', () => this.formView.change())
		this.objectSelector.on('selectOrg', () => this.onSelectOrg())

		// add "All Images" control between the finder and the org tree
		this.objectSelector.orgGardener.$el.find('.finder')
			.after(`<div class="all-images"><div class="wrapper">${icon('images')}${i18n.get('admin.images.allImages')}</div></div>`)

		// add "Upload Image" control between finder and the object tree
		this.objectSelector.objectGardener.$el.find('.finder')
			.after(`<button type="button" class="upload-image button button-default">${icon('upload')}${i18n.get('dashboards.image.pick')}</button>`)
	},

	/**
	 * Callback executed when the user clicks on the "All Images" button
	 */
	allImages: function(e) {
		e.stopPropagation()

		// show all images
		this.objectSelector.showAll()

		// move selected appearance from the tree to the "All Images" button
		this.$('.all-images').addClass('selected')
		this.objectSelector.orgGardener.$('.tree .node').removeClass('selected')

		// disable the upload image button, since an org must be selected to
		// upload an image
		this.$('.upload-image').addClass('is-disabled')
	},

	/**
	 * Callback executed once an org is selected
	 */
	onSelectOrg: function() {
		let orgId = _.result(this.objectSelector.orgGardener.tree.selected(), 'id')
		if (_.isNil(orgId)) {
			return
		}

		// #32347 the "Upload Image" button should be disabled until the object
		// tree is loaded and we have updated permissions
		this.$('.upload-image').addClass('is-disabled')

		// the "All Images" button shouldn't be selected
		this.$('.all-images').removeClass('selected')

		this.listenToOnce(this.objectSelector, 'end', () => {
			// #32347 now that the org's object tree is loaded, the permissions
			// module will have the user's permissions for that org
			let hasPermission = permissions.documents(orgId)
			this.$('.upload-image').toggleClass('is-disabled no-permission', !hasPermission)
		})
	},

	/**
	 * Callback executed when the user clicks on the "Upload Image" button
	 */
	uploadImage: function(e) {
		let target = this.$(e.currentTarget)
		if (target.hasClass('is-disabled')) {
			let content = target.hasClass('no-permission') ? i18n.get('dashboards.image.noPermission') : i18n.get('common.organization.select')
			this.tooltip = new Tooltip({
				attachTo: target,
				content,
				placement: 'below',
				arrow: 'center'
			}).render()
			return
		}

		let organizationId = _.get(this.objectSelector.orgGardener.tree.selected(), 'id') ||
			// if the org tree isn't loaded yet, it will default to the current
			// org
			globals.orgId
		var doc = new File({
			relativeId: _.get(this.objectSelector.objectGardener.tree.selected(), 'id'),
			organizationId,
			type: 'file',
			// we're only displaying images, so the index we send the server
			// will need to be adjusted to be relative to the entire org tree
			// instead when we save
			adjustIndex: true
		})
		let uploadForm = new UploadForm({
			model: doc,
			gardener: this.objectSelector.objectGardener,
			accept: ['image']
		})
		appDisplay.dialog(uploadForm)
		// #58773 after uploading an image, clear the "This organization has no
		// images" message if it's present
		uploadForm.on('done', () => {
			this.objectSelector.clearEmptyState()
		})
	},

	/**
	 * Callback executed when the user clicks on the "Go to Documents" button
	 */
	goToDocuments: function() {
		guard.conditionallyRun(() => {
			let url = `organizations/${globals.orgId}/files`
			conductor.navigate(url, { trigger: true })
		})
	},

	test: function() {
		let selected = this.objectSelector.selected()
		if (!selected || selected.type == 'folder') {
			return i18n.get('documents.images.required')
		}
	},

	submit: function() {
		this.trigger('done', this.objectSelector.selected())
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.objectSelector, this.tooltip], 'remove')
	}

})

export default FileLibrary

FileLibrary.__filename__ = 'components/files/file-library.js'
